import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import {
  PLATFORM_SETTINGS_UPDATED_EVENT,
  loadPlatformSettings,
} from "../lib/platformSettings";

const Footer = () => {
  const location = useLocation();
  const [contact, setContact] = useState({
    organizationEmail: "",
    organizationPhone: "",
    organizationWebsite: "",
    facebookUrl: "",
    instagramUrl: "",
  });

  useEffect(() => {
    let isMounted = true;

    const applySettings = (settings) => {
      if (!isMounted || !settings) return;
      setContact({
        organizationEmail: settings.organizationEmail || "",
        organizationPhone: settings.organizationPhone || "",
        organizationWebsite: settings.organizationWebsite || "",
        facebookUrl: settings.facebookUrl || "",
        instagramUrl: settings.instagramUrl || "",
      });
    };

    const load = async () => {
      try {
        applySettings(await loadPlatformSettings());
      } catch (error) {
        console.warn("Could not load footer contact settings:", error);
      }
    };

    const onUpdated = (event) => applySettings(event.detail);

    load();
    window.addEventListener(PLATFORM_SETTINGS_UPDATED_EVENT, onUpdated);
    return () => {
      isMounted = false;
      window.removeEventListener(PLATFORM_SETTINGS_UPDATED_EVENT, onUpdated);
    };
  }, []);

  const isActive = (path) => location.pathname === path;
  const isEventsActive = location.pathname === "/events" || location.pathname.startsWith("/event");

  const navLinkClass = (active) =>
    `text-[11px] font-bold uppercase tracking-[0.24em] transition ${
      active ? "text-white" : "text-white/75 hover:text-white"
    }`;

  const mailtoHref = contact.organizationEmail ? `mailto:${contact.organizationEmail}` : "";

  return (
    <footer className="bg-brand-maroon border-t border-white/10 text-white">
      <div className="max-w-[1440px] mx-auto px-6 py-5 md:py-6 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        <div>
          <img
            src="/assets/echojham.logo.png"
            alt="Echo Jham Logo"
            className="h-[44px] w-auto object-contain mb-3"
          />
          <p className="text-[14px] text-white/90 font-semibold">Bridging Artist and Audience.</p>
          {(contact.organizationEmail || contact.organizationPhone || contact.organizationWebsite) && (
            <div className="mt-3 space-y-1 text-xs text-white/80">
              {contact.organizationEmail ? (
                <a href={`mailto:${contact.organizationEmail}`} className="block hover:text-white">
                  {contact.organizationEmail}
                </a>
              ) : null}
              {contact.organizationPhone ? <p>{contact.organizationPhone}</p> : null}
              {contact.organizationWebsite ? (
                <a
                  href={contact.organizationWebsite}
                  target="_blank"
                  rel="noreferrer"
                  className="block hover:text-white break-all"
                >
                  {contact.organizationWebsite.replace(/^https?:\/\//, "")}
                </a>
              ) : null}
            </div>
          )}
          <p className="text-xs text-white/75 mt-3 leading-relaxed">
            © 2025 ECHOJHAM Entertainment, Inc.
            <br />
            All rights reserved.
          </p>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-white/15">
            <div className="flex flex-wrap items-center gap-8 md:gap-10">
              <Link to="/" className={navLinkClass(isActive("/"))}>Home</Link>
              <Link to="/events" className={navLinkClass(isEventsActive)}>Events</Link>
              <Link to="/tickets" className={navLinkClass(isActive("/tickets"))}>My Ticket</Link>
              <Link to="/about" className={navLinkClass(isActive("/about"))}>About</Link>
            </div>

            <div className="w-full sm:w-[240px]">
              <input
                type="text"
                placeholder="Search..."
                className="w-full h-9 rounded-full border border-white/25 bg-white text-black px-4 text-xs focus:outline-none"
              />
            </div>
          </div>

          <div className="pt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-8 md:gap-10">
              {mailtoHref ? (
                <a
                  href={mailtoHref}
                  className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/90 hover:text-white"
                >
                  Contact Us
                </a>
              ) : (
                <span className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/60">
                  Contact Us
                </span>
              )}
              <Link to="/faqs" className="text-[11px] font-bold uppercase tracking-[0.24em] text-white/90 hover:text-white">
                FAQs
              </Link>
            </div>

            <div className="flex items-center gap-4">
              {contact.facebookUrl ? (
                <a
                  href={contact.facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/90 hover:text-white"
                  aria-label="Facebook"
                >
                  <Facebook size={26} strokeWidth={2.2} />
                </a>
              ) : (
                <span className="text-white/40" aria-label="Facebook">
                  <Facebook size={26} strokeWidth={2.2} />
                </span>
              )}
              {contact.instagramUrl ? (
                <a
                  href={contact.instagramUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/90 hover:text-white"
                  aria-label="Instagram"
                >
                  <Instagram size={26} strokeWidth={2.2} />
                </a>
              ) : (
                <span className="text-white/40" aria-label="Instagram">
                  <Instagram size={26} strokeWidth={2.2} />
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
